'use client';

import { useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, Eye } from 'lucide-react';

import { usePageBuilderContext } from './page-builder-context';
import { FixedHeader } from './header/fixed-header';
import { BlockList } from './blocks/block-list';
import { BlockInsertMenu } from './blocks/block-insert-menu';
import { MetadataSection } from './metadata/metadata-section';
import { TeamSection } from './team/team-section';
import { ActionBar } from './actions/action-bar';
import { PreviewPanel } from './preview/preview-panel';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

type ViewMode = 'edit' | 'preview';

// ============================================================================
// KEYBOARD SHORTCUTS
// ============================================================================

function useKeyboardShortcuts(viewMode: ViewMode, setViewMode: (mode: ViewMode) => void) {
  const { undo, redo, canUndo, canRedo, submit } = usePageBuilderContext();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Cmd/Ctrl + Z = Undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        if (canUndo && viewMode === 'edit') {
          e.preventDefault();
          undo();
        }
      }

      // Cmd/Ctrl + Shift + Z = Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        if (canRedo && viewMode === 'edit') {
          e.preventDefault();
          redo();
        }
      }

      // Cmd/Ctrl + Y = Redo (alternative)
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        if (canRedo && viewMode === 'edit') {
          e.preventDefault();
          redo();
        }
      }

      // Cmd/Ctrl + Enter = Submit
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        submit();
      }

      // Cmd/Ctrl + P = Toggle Preview
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault();
        setViewMode(viewMode === 'edit' ? 'preview' : 'edit');
      }
    },
    [undo, redo, canUndo, canRedo, submit, viewMode, setViewMode]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

// ============================================================================
// VIEW MODE TABS
// ============================================================================

interface ViewModeTabsProps {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

function ViewModeTabs({ viewMode, setViewMode }: ViewModeTabsProps) {
  return (
    <div className="flex items-center justify-center">
      <div className="inline-flex items-center p-1 bg-muted rounded-lg">
        <button
          type="button"
          onClick={() => setViewMode('edit')}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
            viewMode === 'edit'
              ? 'bg-background text-foreground'
              : 'text-foreground-secondary hover:text-foreground'
          )}
        >
          <Pencil className="h-4 w-4" />
          <span>Редактор</span>
        </button>
        <button
          type="button"
          onClick={() => setViewMode('preview')}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
            viewMode === 'preview'
              ? 'bg-background text-foreground'
              : 'text-foreground-secondary hover:text-foreground'
          )}
        >
          <Eye className="h-4 w-4" />
          <span>Превью</span>
        </button>
      </div>
      <div className="hidden sm:block ml-3">
        <kbd className="px-2 py-1 rounded bg-muted text-foreground-tertiary text-xs font-mono">
          ⌘P
        </kbd>
      </div>
    </div>
  );
}

// ============================================================================
// EDIT VIEW
// ============================================================================

function EditView() {
  const { mode } = usePageBuilderContext();

  return (
    <div className="min-h-screen pb-24">
      {/* Main content area */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-8">
        {/* Fixed Header (name, tagline, logo) */}
        <div id="section-header">
          <FixedHeader />
        </div>

        {/* Block Editor */}
        <div id="section-content">
          <BlockList />
        </div>

        {/* Metadata Section (categories, status tags) */}
        <div id="section-metadata">
          <MetadataSection />
        </div>

        {/* Team Section (invite team members) — create only; team management
            for existing products lives on the product page. */}
        {mode === 'create' && (
          <div>
            <TeamSection />
          </div>
        )}
      </div>

      {/* Floating Action Bar */}
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <ActionBar />
      </div>

      {/* Block Insert Menu (modal) */}
      <BlockInsertMenu />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PageBuilderForm() {
  const [viewMode, setViewMode] = useState<ViewMode>('edit');

  // Register keyboard shortcuts
  useKeyboardShortcuts(viewMode, setViewMode);

  return (
    <div className="min-h-screen">
      {/* View Mode Tabs */}
      <div className="sticky top-14 z-30 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <ViewModeTabs viewMode={viewMode} setViewMode={setViewMode} />
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {viewMode === 'edit' ? (
          <motion.div
            key="edit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
          >
            <EditView />
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
          >
            <PreviewPanel />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
